import { Button, Select, Text, Group } from '@mantine/core';
import { useIntl } from 'react-intl';

interface PaginationControlsProps {
  totalItems: number;
  currentPage: number;
  itemsPerPage: number;
  totalPages: number;
  setCurrentPage: (page: number) => void;
  setItemsPerPage: (itemsPerPage: number) => void;
}

export const PaginationControls = ({
  totalItems,
  currentPage,
  itemsPerPage,
  totalPages,
  setCurrentPage,
  setItemsPerPage,
}: PaginationControlsProps) => {
  const intl = useIntl();

  if (totalItems === 0) return null;

  return (
    <Group justify="space-between" align="center" wrap="wrap" gap="md">
      {/* Showing items text */}
      <Text size="sm" c="dimmed">
        {intl.formatMessage({ id: 'showing' })}{' '}
        {(currentPage - 1) * itemsPerPage + 1} -{' '}
        {Math.min(currentPage * itemsPerPage, totalItems)}{' '}
        {intl.formatMessage({ id: 'of' })} {totalItems}{' '}
        {intl.formatMessage({ id: 'folders' }).toLowerCase()}
      </Text>

      {/* Controls container */}
      <Group gap="md" wrap="wrap" justify="center">
        {/* Items per page picker */}
        <Group gap="xs">
          <Text size="sm" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
            {intl.formatMessage({ id: 'itemsPerPage' })}:
          </Text>
          <Select
            size="xs"
            w={{ base: 80, sm: 100 }}
            value={itemsPerPage.toString()}
            onChange={(value) => {
              const newValue = Number(value);
              if (newValue > 0) {
                setItemsPerPage(newValue);
                setCurrentPage(1);
              }
            }}
            data={[
              { value: '2', label: '2' },
              { value: '5', label: '5' },
              { value: '10', label: '10' },
              { value: '25', label: '25' },
              { value: '50', label: '50' },
              { value: '100', label: '100' },
            ]}
          />
        </Group>

        {/* Pagination buttons */}
        <Group gap="xs">
          <Button.Group>
            <Button
              id="pagination-previous-button"
              size="xs"
              variant="default"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(currentPage - 1)}
            >
              {intl.formatMessage({ id: 'previous' })}
            </Button>
            <Button
              id="pagination-next-button"
              size="xs"
              variant="default"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(currentPage + 1)}
            >
              {intl.formatMessage({ id: 'next' })}
            </Button>
          </Button.Group>
          <Text size="sm" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
            {intl.formatMessage({ id: 'page' })} {currentPage}{' '}
            {intl.formatMessage({ id: 'of' })} {totalPages}
          </Text>
        </Group>
      </Group>
    </Group>
  );
};
